import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  CustomInput,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner
} from 'reactstrap'
import {
  DELETION_REASONS,
  DELETION_REASON_OTHER,
  DELETION_STATUS_COLORS,
  DELETION_STATUS_LABELS,
  fetchMyAccountDeletionRequest,
  submitMyAccountDeletionRequest
} from '../../../helpers/api/accountDeletion'
import { ERROR_RESPONSE, SUCCESS_RESPONSE } from '../../../helpers/codeResponses/codes'
import handleErrorMessage from '../../../helpers/errors/handleErrorMessage'
import ButtonIcon from '../../common/ButtonIcon'
import { Toast } from '../../UI/toast/Toast'

/**
 * Bloque "Gestionar cuenta" del perfil: agrupa las acciones sobre la cuenta
 * en si (hoy, la solicitud de eliminacion) separadas de la edicion de datos.
 */
const ProfileAccountManagement = ({ email }) => {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [request, setRequest] = useState(null)
  const [isOpen, setIsOpen] = useState(false)

  const [reason, setReason] = useState('')
  const [reasonDetail, setReasonDetail] = useState('')
  const [confirm, setConfirm] = useState(false)

  const loadRequest = (isMounted = true) => {
    isMounted && setLoading(true)
    return fetchMyAccountDeletionRequest()
      .then(({ data }) => {
        isMounted && setRequest(data)
      })
      .catch(() => {
        isMounted && setRequest(null)
      })
      .finally(() => {
        isMounted && setLoading(false)
      })
  }

  useEffect(() => {
    let isMounted = true
    loadRequest(isMounted)
    return () => {
      isMounted = false
    }
  }, [])

  const toggle = () => {
    setIsOpen(!isOpen)
    setReason('')
    setReasonDetail('')
    setConfirm(false)
  }

  const send = () => {
    setSubmitting(true)
    submitMyAccountDeletionRequest({
      reason: reason === DELETION_REASON_OTHER ? reasonDetail.trim() : reason,
      confirm: true
    })
      .then(({ code }) => {
        if (code === SUCCESS_RESPONSE.CREATED) {
          Toast.fire({
            type: 'success',
            title: 'Tu solicitud fue registrada, nuestro equipo la revisará.'
          })
          toggle()
          loadRequest()
        }
      })
      .catch((error) => {
        const data = error?.data || error
        if (data?.code === ERROR_RESPONSE.PENDING_ALREADY_EXIST) {
          Toast.fire({ type: 'error', title: 'Ya tienes una solicitud en curso.' })
          toggle()
          loadRequest()
          return
        }
        handleErrorMessage(error)
      })
      .finally(() => setSubmitting(false))
  }

  const closeBtn = (
    <Button
      className="close font-weight-normal bg-transparent border-0 shadow-none"
      onClick={toggle}
    >
      &times;
    </Button>
  )

  return (
    <Card className="mb-3 bg-light">
      <CardBody>
        <CardTitle tag="h5">Gestionar cuenta</CardTitle>

        {loading ? (
          <Spinner size="sm" />
        ) : (
          <Row className="align-items-center border-top pt-3">
            <Col>
              <p className="mb-1 font-weight-semi-bold">Eliminar mi cuenta</p>
              <p className="fs--1 text-600 mb-0">
                {request?.is_open
                  ? `Solicitud registrada el ${moment(request.created_at).format('DD/MM/YYYY')}. ${
                      email
                        ? 'Te escribiremos a tu correo cuando esté lista.'
                        : 'El equipo te contactará cuando esté lista.'
                    }`
                  : 'Solicita la eliminación de tu cuenta y de la información asociada.'}
              </p>
            </Col>
            <Col xs="auto" className="mt-2 mt-sm-0">
              {request?.is_open ? (
                <Badge color={`soft-${DELETION_STATUS_COLORS[request.status]}`} className="fs--2">
                  {DELETION_STATUS_LABELS[request.status] || request.status}
                </Badge>
              ) : (
                <ButtonIcon
                  color="falcon-danger"
                  size="sm"
                  icon={faTrashAlt}
                  onClick={() => setIsOpen(true)}
                >
                  Eliminar cuenta
                </ButtonIcon>
              )}
            </Col>
          </Row>
        )}
      </CardBody>

      <Modal isOpen={isOpen} toggle={toggle} centered>
        <ModalHeader toggle={toggle} close={closeBtn}>
          Eliminar mi cuenta
        </ModalHeader>
        <ModalBody className="fs--1">
          <Alert color="warning" className="fs--1">
            Al eliminar tu cuenta se borran tus datos personales, tu perfil y tu historial de
            accesos. Cierta información asociada a pagos y estados de cuenta puede conservarse el
            tiempo que exija la normativa contable aplicable. Procesamos la solicitud en un plazo
            máximo de 30 días hábiles y {email ? 'te avisamos por correo' : 'el equipo te contactará'}.
          </Alert>

          <Row form>
            <Col tag={FormGroup} sm={12}>
              <Label>Lamentamos que te vayas. Cuéntanos el motivo:</Label>
              <Input
                type="select"
                value={reason}
                onChange={({ target }) => setReason(target.value)}
              >
                <option value="">Selecciona un motivo...</option>
                {DELETION_REASONS.map((label) => (
                  <option key={label} value={label}>
                    {label}
                  </option>
                ))}
                <option value={DELETION_REASON_OTHER}>{DELETION_REASON_OTHER}</option>
              </Input>
            </Col>
          </Row>
          {reason === DELETION_REASON_OTHER && (
            <Row form>
              <Col tag={FormGroup} sm={12}>
                <Label>¿Cuál es el motivo?</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={reasonDetail}
                  placeholder="Cuéntanos por qué deseas eliminarla..."
                  onChange={({ target }) => setReasonDetail(target.value)}
                />
              </Col>
            </Row>
          )}
          <FormGroup className="mb-0">
            <CustomInput
              type="checkbox"
              id="confirm-my-account-deletion"
              checked={confirm}
              onChange={({ target }) => setConfirm(target.checked)}
              label="Entiendo que esta acción es irreversible y que perderé el acceso a mi cuenta y a la información asociada."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="falcon-default" onClick={toggle} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            color="danger"
            onClick={send}
            disabled={
              !confirm ||
              !reason ||
              (reason === DELETION_REASON_OTHER && !reasonDetail.trim()) ||
              submitting
            }
          >
            {submitting ? (
              <Spinner size="sm" className="mr-2" />
            ) : (
              <FontAwesomeIcon icon={faTrashAlt} className="mr-2" />
            )}
            Enviar solicitud
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  )
}

export default ProfileAccountManagement
